import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PregnancyDashboard.css';
import { useProfile } from '../../../context/ProfileContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBaby,
  faCalendarDays,
  faWeightScale,
  faPen,
  faTrash,
  faVenus,
  faPlus,
  faSpinner,
  faTriangleExclamation,
  faChartLine,
  faSeedling,
  faClipboardList,
  faFaceSmile,
  faNotesMedical,
  faCheck,
  faXmark,
  faArrowRight,
  faFlagCheckered,
  faClockRotateLeft,
  faHeartPulse,
  faFileLines,
} from '@fortawesome/free-solid-svg-icons';
import { Container } from 'react-bootstrap';
import BackButton from '../../../components/BackButton/BackButton';
import Modal from '../../../components/Modal/Modal';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import womensHealthService, {
  BirthPlan,
  KickCounter,
  PregnancyCheckup,
  PregnancyDocument,
  PregnancyProfile,
  PregnancyWeekLog,
  Symptom,
} from '../../../services/womensHealthService';

type DashTab = 'this-week' | 'log-week' | 'weight-history' | 'fetal-movement';

interface ToastState {
  message: string;
  type: 'success' | 'error';
}

/* ------------------------------------------------------------------ */
/*  BABY DEVELOPMENT DATA                                              */
/* ------------------------------------------------------------------ */

interface BabyDevelopment {
  size: string;
  fruit: string;
  milestone: string;
}

const babyDevelopmentMap: Record<number, BabyDevelopment> = {
  4: { size: '0.04 in', fruit: 'Poppy seed', milestone: 'Neural tube forming, heart starts to develop' },
  5: { size: '0.05 in', fruit: 'Sesame seed', milestone: 'Brain and spinal cord developing rapidly' },
  6: { size: '0.25 in', fruit: 'Lentil', milestone: 'Nose, mouth, and ears taking shape' },
  7: { size: '0.5 in', fruit: 'Blueberry', milestone: 'Arms and legs forming, brain growing fast' },
  8: { size: '0.63 in', fruit: 'Raspberry', milestone: 'Fingers and toes developing, baby starts moving' },
  9: { size: '0.9 in', fruit: 'Cherry', milestone: 'Organs formed, bones begin to harden' },
  10: { size: '1.2 in', fruit: 'Strawberry', milestone: 'Vital organs functioning, nails start forming' },
  11: { size: '1.6 in', fruit: 'Fig', milestone: 'Baby can open and close fists, tooth buds appear' },
  12: { size: '2.1 in', fruit: 'Lime', milestone: 'Reflexes developing, can suck thumb' },
  13: { size: '2.9 in', fruit: 'Peach', milestone: 'Fingerprints formed, vocal cords developing' },
  14: { size: '3.4 in', fruit: 'Lemon', milestone: 'Facial expressions possible, hair growing' },
  15: { size: '4 in', fruit: 'Apple', milestone: 'Can sense light, legs longer than arms' },
  16: { size: '4.6 in', fruit: 'Avocado', milestone: 'Hearing develops, can feel touch' },
  17: { size: '5.1 in', fruit: 'Pear', milestone: 'Skeleton hardening, fat stores developing' },
  18: { size: '5.6 in', fruit: 'Bell pepper', milestone: 'Baby can yawn and hiccup, ears in position' },
  19: { size: '6 in', fruit: 'Mango', milestone: 'Senses developing rapidly, unique fingerprints' },
  20: { size: '6.5 in', fruit: 'Banana', milestone: 'Halfway there! Can swallow, taste buds working' },
  21: { size: '10.5 in', fruit: 'Carrot', milestone: 'Eyebrows visible, movement patterns established' },
  22: { size: '10.9 in', fruit: 'Papaya', milestone: 'Eyes formed, grip is strong, can hear you' },
  23: { size: '11.4 in', fruit: 'Grapefruit', milestone: 'Skin becomes less translucent, lungs developing' },
  24: { size: '11.8 in', fruit: 'Corn', milestone: 'Inner ear fully developed, sense of balance' },
  25: { size: '13.6 in', fruit: 'Cauliflower', milestone: 'Baby responds to voice, hair has color' },
  26: { size: '14 in', fruit: 'Lettuce', milestone: 'Eyes can open, inhaling amniotic fluid for practice' },
  27: { size: '14.4 in', fruit: 'Rutabaga', milestone: 'Brain very active, regular sleep cycles' },
  28: { size: '14.8 in', fruit: 'Eggplant', milestone: 'Can blink, dream during REM sleep' },
  29: { size: '15.2 in', fruit: 'Butternut squash', milestone: 'Bones fully developed, muscles and lungs maturing' },
  30: { size: '15.7 in', fruit: 'Cabbage', milestone: 'Brain growing rapidly, can regulate body temp' },
  31: { size: '16.2 in', fruit: 'Coconut', milestone: 'All five senses working, movements are strong' },
  32: { size: '16.7 in', fruit: 'Squash', milestone: 'Practicing breathing, toenails and hair growing' },
  33: { size: '17.2 in', fruit: 'Pineapple', milestone: 'Bones hardening, immune system developing' },
  34: { size: '17.7 in', fruit: 'Cantaloupe', milestone: 'Central nervous system maturing, fat layers building' },
  35: { size: '18.2 in', fruit: 'Honeydew', milestone: 'Kidneys fully developed, liver can process waste' },
  36: { size: '18.7 in', fruit: 'Romaine lettuce', milestone: 'Gaining weight rapidly, head may engage in pelvis' },
  37: { size: '19.1 in', fruit: 'Swiss chard', milestone: 'Considered early term, organs ready for outside world' },
  38: { size: '19.6 in', fruit: 'Leek', milestone: 'Firm grasp, brain and lungs continue maturing' },
  39: { size: '20 in', fruit: 'Pumpkin', milestone: 'Full term! All organs matured, ready for birth' },
  40: { size: '20.2 in', fruit: 'Watermelon', milestone: 'Due date! Baby is fully ready to meet you' },
};

const getBabyDevelopment = (week: number): BabyDevelopment => {
  if (week < 4) return { size: 'Tiny', fruit: 'Grain of sand', milestone: 'Cells are rapidly dividing and implanting' };
  if (week > 40) return babyDevelopmentMap[40];
  return babyDevelopmentMap[week] || babyDevelopmentMap[Math.min(week, 40)];
};

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                          */
/* ------------------------------------------------------------------ */

const PregnancyDashboard: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  const [pregnancy, setPregnancy] = useState<PregnancyProfile | null>(null);
  const [weekLogs, setWeekLogs] = useState<PregnancyWeekLog[]>([]);
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [choices, setChoices] = useState<any>(null);
  const [weekLogChoices, setWeekLogChoices] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<DashTab>((searchParams.get('tab') as DashTab) || 'this-week');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; confirmLabel?: string; variant?: 'danger' | 'warning' | 'primary'; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // Past pregnancy episodes (delivered/loss), shown as history
  const [history, setHistory] = useState<PregnancyProfile[]>([]);

  // End-pregnancy (delivered / loss) modal
  const [endOpen, setEndOpen] = useState(false);
  const [endForm, setEndForm] = useState<{
    status: 'delivered' | 'loss';
    delivery_date: string;
    delivery_type: string;
    outcome_notes: string;
  }>({ status: 'delivered', delivery_date: '', delivery_type: 'undecided', outcome_notes: '' });
  const [endingPregnancy, setEndingPregnancy] = useState(false);

  // Just-ended episode, kept so we can offer the postpartum hand-off
  const [justEnded, setJustEnded] = useState<PregnancyProfile | null>(null);

  // Read-only detail view of a past pregnancy
  const [historyPregnancy, setHistoryPregnancy] = useState<PregnancyProfile | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<PregnancyWeekLog[]>([]);
  const [historyCheckups, setHistoryCheckups] = useState<PregnancyCheckup[]>([]);
  const [historyDocs, setHistoryDocs] = useState<PregnancyDocument[]>([]);
  const [historyKicks, setHistoryKicks] = useState<KickCounter[]>([]);
  const [historyBirthPlan, setHistoryBirthPlan] = useState<BirthPlan | null>(null);

  // Setup form
  const [setupForm, setSetupForm] = useState({ lmp_date: '', delivery_type: 'undecided' });
  const [submittingSetup, setSubmittingSetup] = useState(false);

  // Edit-profile (LMP / delivery type) modal
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ lmp_date: '', delivery_type: 'undecided' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Week log form
  const [logForm, setLogForm] = useState({
    week_number: 1,
    weight: '',
    mood: '',
    fetal_movement_count: '',
    symptoms: [] as number[],
    notes: '',
  });
  const [submittingLog, setSubmittingLog] = useState(false);

  // Edit state
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    weight: '',
    mood: '',
    fetal_movement_count: '',
    notes: '',
  });

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [preg, profileChoices, wlChoices, syms, allProfiles] = await Promise.all([
        womensHealthService.getActivePregnancy(),
        womensHealthService.getPregnancyProfileChoices(),
        womensHealthService.getPregnancyWeekLogChoices(),
        womensHealthService.getSymptomsByCategory('pregnancy'),
        womensHealthService.getPregnancyProfiles(),
      ]);
      setPregnancy(preg);
      setChoices(profileChoices);
      setWeekLogChoices(wlChoices);
      setSymptoms(syms);
      setHistory(
        (Array.isArray(allProfiles) ? allProfiles : [])
          .filter((p) => p.status !== 'active')
          .sort((a, b) => String(b.delivery_date || b.created_at).localeCompare(String(a.delivery_date || a.created_at)))
      );
      // A new active pregnancy supersedes the "just ended" hand-off banner.
      if (preg) setJustEnded(null);

      if (preg) {
        const logs = await womensHealthService.getPregnancyWeekLogs(preg.id);
        setWeekLogs(Array.isArray(logs) ? logs : []);
        setLogForm((prev) => ({
          ...prev,
          week_number: preg.current_week || 1,
        }));
      }
    } catch (err: any) {
      setError('Failed to load pregnancy data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeProfile]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupForm.lmp_date) return;
    setSubmittingSetup(true);
    try {
      await womensHealthService.createPregnancyProfile(setupForm);
      showToast('Pregnancy profile created successfully!', 'success');
      loadData();
    } catch {
      showToast('Failed to create pregnancy profile.', 'error');
    } finally {
      setSubmittingSetup(false);
    }
  };

  const handleProfileEditStart = () => {
    if (!pregnancy) return;
    setProfileForm({
      lmp_date: pregnancy.lmp_date || '',
      delivery_type: pregnancy.delivery_type || 'undecided',
    });
    setEditProfileOpen(true);
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pregnancy || !profileForm.lmp_date) return;
    setSavingProfile(true);
    try {
      await womensHealthService.updatePregnancyProfile(pregnancy.id, profileForm);
      showToast('Pregnancy details updated.', 'success');
      setEditProfileOpen(false);
      loadData();
    } catch {
      showToast('Failed to update pregnancy details.', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleProfileDelete = () => {
    if (!pregnancy) return;
    setConfirmDialog({
      open: true,
      title: 'Delete Pregnancy Profile',
      message: 'This permanently removes this pregnancy and all its records (week logs, checkups, documents). This cannot be undone. Continue?',
      confirmLabel: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await womensHealthService.deletePregnancyProfile(pregnancy.id);
          showToast('Pregnancy profile deleted.', 'success');
          setPregnancy(null);
          setWeekLogs([]);
          setJustEnded(null);
          loadData();
        } catch {
          showToast('Failed to delete pregnancy profile.', 'error');
        }
      },
    });
  };

  const handleEndStart = () => {
    if (!pregnancy) return;
    setEndForm({
      status: 'delivered',
      delivery_date: todayIso(),
      delivery_type: pregnancy.delivery_type || 'undecided',
      outcome_notes: '',
    });
    setEndOpen(true);
  };

  const performEndPregnancy = async () => {
    if (!pregnancy) return;
    setEndingPregnancy(true);
    try {
      const updated = await womensHealthService.deliverPregnancy(pregnancy.id, {
        status: endForm.status,
        delivery_date: endForm.delivery_date,
        delivery_type: endForm.status === 'delivered' ? endForm.delivery_type : undefined,
        outcome_notes: endForm.outcome_notes,
      });
      showToast(
        endForm.status === 'delivered'
          ? 'Pregnancy marked as delivered.'
          : 'Pregnancy recorded as loss.',
        'success'
      );
      setJustEnded(updated);
      setEndOpen(false);
      setPregnancy(null);
      setWeekLogs([]);
      loadData();
    } catch {
      showToast('Failed to update pregnancy.', 'error');
    } finally {
      setEndingPregnancy(false);
    }
  };

  const handleEndSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pregnancy || !endForm.delivery_date) return;
    if (endForm.status === 'loss') {
      setConfirmDialog({
        open: true,
        title: 'Record this loss?',
        message:
          'This closes the pregnancy and saves it privately to your history as a loss. You can continue whenever you are ready.',
        confirmLabel: 'Record loss',
        variant: 'warning',
        onConfirm: () => {
          void performEndPregnancy();
        },
      });
      return;
    }
    void performEndPregnancy();
  };

  const openHistoryDetail = async (profile: PregnancyProfile) => {
    setHistoryPregnancy(profile);
    setHistoryLoading(true);
    setHistoryLogs([]);
    setHistoryCheckups([]);
    setHistoryDocs([]);
    setHistoryKicks([]);
    setHistoryBirthPlan(null);
    try {
      const [logs, checks, docs, kicks, plans] = await Promise.all([
        womensHealthService.getPregnancyWeekLogs(profile.id),
        womensHealthService.getPregnancyCheckups(profile.id),
        womensHealthService.getPregnancyDocuments(profile.id),
        womensHealthService.getKickCounters(profile.id),
        womensHealthService.getBirthPlans(profile.id),
      ]);
      setHistoryLogs(Array.isArray(logs) ? logs : []);
      setHistoryCheckups(Array.isArray(checks) ? checks : []);
      setHistoryDocs(Array.isArray(docs) ? docs : []);
      setHistoryKicks(Array.isArray(kicks) ? kicks : []);
      setHistoryBirthPlan(Array.isArray(plans) && plans.length > 0 ? plans[0] : null);
    } catch {
      showToast('Failed to load this pregnancy.', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryDetail = () => setHistoryPregnancy(null);

  const handleLogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pregnancy) return;
    setSubmittingLog(true);
    try {
      const payload = {
        pregnancy: pregnancy.id,
        week_number: logForm.week_number,
        weight: logForm.weight ? parseFloat(logForm.weight) : null,
        mood: logForm.mood || '',
        fetal_movement_count: logForm.fetal_movement_count
          ? parseInt(logForm.fetal_movement_count, 10)
          : null,
        symptoms: logForm.symptoms,
        notes: logForm.notes,
      };
      await womensHealthService.createPregnancyWeekLog(payload);
      showToast('Week log saved successfully!', 'success');
      setLogForm({
        week_number: pregnancy.current_week || 1,
        weight: '',
        mood: '',
        fetal_movement_count: '',
        symptoms: [],
        notes: '',
      });
      const logs = await womensHealthService.getPregnancyWeekLogs(pregnancy.id);
      setWeekLogs(Array.isArray(logs) ? logs : []);
      setActiveTab('this-week');
    } catch {
      showToast('Failed to save week log.', 'error');
    } finally {
      setSubmittingLog(false);
    }
  };

  const handleDeleteLog = (logId: number) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Log',
      message: 'Are you sure you want to delete this week log?',
      onConfirm: async () => {
        try {
          await womensHealthService.deletePregnancyWeekLog(logId);
          showToast('Log deleted.', 'success');
          if (pregnancy) {
            const logs = await womensHealthService.getPregnancyWeekLogs(pregnancy.id);
            setWeekLogs(Array.isArray(logs) ? logs : []);
          }
        } catch {
          showToast('Failed to delete log.', 'error');
        }
      },
    });
  };

  const handleEditStart = (log: PregnancyWeekLog) => {
    setEditingLogId(log.id);
    setEditForm({
      weight: log.weight?.toString() || '',
      mood: log.mood || '',
      fetal_movement_count: log.fetal_movement_count?.toString() || '',
      notes: log.notes || '',
    });
  };

  const handleEditSave = async (logId: number) => {
    try {
      await womensHealthService.updatePregnancyWeekLog(logId, {
        weight: editForm.weight ? parseFloat(editForm.weight) : null,
        mood: editForm.mood,
        fetal_movement_count: editForm.fetal_movement_count
          ? parseInt(editForm.fetal_movement_count, 10)
          : null,
        notes: editForm.notes,
      });
      showToast('Log updated.', 'success');
      setEditingLogId(null);
      if (pregnancy) {
        const logs = await womensHealthService.getPregnancyWeekLogs(pregnancy.id);
        setWeekLogs(Array.isArray(logs) ? logs : []);
      }
    } catch {
      showToast('Failed to update log.', 'error');
    }
  };

  const toggleSymptom = (symptomId: number) => {
    setLogForm((prev) => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptomId)
        ? prev.symptoms.filter((s) => s !== symptomId)
        : [...prev.symptoms, symptomId],
    }));
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const todayIso = () => new Date().toISOString().split('T')[0];

  const latestLog = weekLogs.length > 0
    ? weekLogs.reduce((a, b) => (a.week_number >= b.week_number ? a : b))
    : null;

  // Effective week drives the hero, progress, and baby-size. Prefer the latest
  // logged week (what the user is actively tracking); fall back to the
  // LMP-computed current_week, then 1. This keeps baby-size in sync with logs.
  const effectiveWeek = latestLog?.week_number || pregnancy?.current_week || 1;

  const progressPercent = Math.min((effectiveWeek / 40) * 100, 100);

  const getTrimester = (week: number): string => {
    if (week <= 13) return '1st Trimester';
    if (week <= 26) return '2nd Trimester';
    return '3rd Trimester';
  };

  const tabs = [
    { id: 'this-week' as DashTab, label: 'This Week', icon: faClipboardList },
    { id: 'log-week' as DashTab, label: 'Log Week', icon: faPen },
    { id: 'weight-history' as DashTab, label: 'Weight History', icon: faChartLine },
    { id: 'fetal-movement' as DashTab, label: 'Fetal Movement', icon: faBaby },
  ];

  const renderHistory = () => {
    if (history.length === 0) return null;
    return (
      <div className="wh-preg-history">
        <h3 className="wh-preg-history-title">
          <FontAwesomeIcon icon={faClockRotateLeft} /> Previous Pregnancies
        </h3>
        <div className="wh-preg-history-list">
          {history.map((p) => (
            <button
              key={p.id}
              type="button"
              className="wh-preg-history-item"
              onClick={() => openHistoryDetail(p)}
            >
              <span className={`wh-preg-history-badge wh-preg-history-badge-${p.status}`}>
                {p.status_display || p.status}
              </span>
              <div className="wh-preg-history-dates">
                {p.lmp_date && <span>LMP: {formatDate(p.lmp_date)}</span>}
                {p.delivery_date && <span>Ended: {formatDate(p.delivery_date)}</span>}
                {!p.delivery_date && p.edd && <span>Due: {formatDate(p.edd)}</span>}
              </div>
              <div className="wh-preg-history-meta">
                {p.weeks_carried !== null && (
                  <span><strong>{p.weeks_carried}</strong> weeks</span>
                )}
                {p.delivery_type && p.delivery_type !== 'undecided' && (
                  <span>{p.delivery_type_display || p.delivery_type}</span>
                )}
                {p.outcome_notes && <span>{p.outcome_notes}</span>}
              </div>
              <div className="wh-preg-history-counts">
                <span><FontAwesomeIcon icon={faClipboardList} /> {p.record_counts?.week_logs ?? 0} weeks logged</span>
                <span><FontAwesomeIcon icon={faChartLine} /> {p.record_counts?.checkups ?? 0} check-ups</span>
                <span><FontAwesomeIcon icon={faFileLines} /> {p.record_counts?.documents ?? 0} documents</span>
              </div>
              <div className="wh-preg-history-view">
                <FontAwesomeIcon icon={faArrowRight} /> View this pregnancy
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderHistoryDetail = () => {
    if (!historyPregnancy) return null;
    const sortedLogs = [...historyLogs].sort((a, b) => a.week_number - b.week_number);
    return (
      <Modal
        open={!!historyPregnancy}
        onClose={closeHistoryDetail}
        title={
          historyPregnancy.status === 'loss'
            ? 'Pregnancy — Loss'
            : 'Pregnancy — Delivered'
        }
        icon={faBaby}
        size="lg"
        footer={
          <button className="wh-preg-ghost-btn" onClick={closeHistoryDetail}>
            Close
          </button>
        }
      >
        {historyLoading ? (
          <div className="wh-preg-loading">
            <FontAwesomeIcon icon={faSpinner} spin className="wh-preg-loading-icon" />
            <p>Loading pregnancy records...</p>
          </div>
        ) : (
          <div className="wh-preg-history-detail">
            <div className="wh-preg-detail-summary">
              <span className={`wh-preg-history-badge wh-preg-history-badge-${historyPregnancy.status}`}>
                {historyPregnancy.status_display || historyPregnancy.status}
              </span>
              <div className="wh-preg-detail-grid">
                <div>
                  <span className="wh-preg-detail-label">LMP</span>
                  <span className="wh-preg-detail-value">{historyPregnancy.lmp_date ? formatDate(historyPregnancy.lmp_date) : '--'}</span>
                </div>
                <div>
                  <span className="wh-preg-detail-label">Due Date</span>
                  <span className="wh-preg-detail-value">{historyPregnancy.edd ? formatDate(historyPregnancy.edd) : '--'}</span>
                </div>
                <div>
                  <span className="wh-preg-detail-label">Ended</span>
                  <span className="wh-preg-detail-value">{historyPregnancy.delivery_date ? formatDate(historyPregnancy.delivery_date) : '--'}</span>
                </div>
                <div>
                  <span className="wh-preg-detail-label">Weeks Carried</span>
                  <span className="wh-preg-detail-value">{historyPregnancy.weeks_carried ?? '--'}</span>
                </div>
                <div>
                  <span className="wh-preg-detail-label">Delivery Type</span>
                  <span className="wh-preg-detail-value">
                    {historyPregnancy.delivery_type && historyPregnancy.delivery_type !== 'undecided'
                      ? historyPregnancy.delivery_type_display || historyPregnancy.delivery_type
                      : '--'}
                  </span>
                </div>
              </div>
              {historyPregnancy.outcome_notes && (
                <p className="wh-preg-detail-notes">
                  <FontAwesomeIcon icon={faNotesMedical} /> {historyPregnancy.outcome_notes}
                </p>
              )}
            </div>

            <h4 className="wh-preg-detail-section">
              <FontAwesomeIcon icon={faClipboardList} /> Weekly Logs ({sortedLogs.length})
            </h4>
            {sortedLogs.length === 0 ? (
              <p className="wh-preg-detail-empty">No weekly logs recorded for this pregnancy.</p>
            ) : (
              <div className="wh-preg-table-wrapper">
                <table className="wh-preg-table">
                  <thead>
                    <tr>
                      <th>Week</th>
                      <th>Weight</th>
                      <th>Mood</th>
                      <th>Fetal Movements</th>
                      <th>Symptoms</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLogs.map((log) => (
                      <tr key={log.id}>
                        <td><span className="wh-preg-week-badge">Week {log.week_number}</span></td>
                        <td>{log.weight ? `${log.weight} kg` : '-'}</td>
                        <td>{log.mood_display || log.mood || '-'}</td>
                        <td>{log.fetal_movement_count ?? '-'}</td>
                        <td>
                          {log.symptom_details && log.symptom_details.length > 0
                            ? log.symptom_details.map((s) => s.name).join(', ')
                            : '-'}
                        </td>
                        <td>{log.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h4 className="wh-preg-detail-section">
              <FontAwesomeIcon icon={faCalendarDays} /> Check-ups ({historyCheckups.length})
            </h4>
            {historyCheckups.length === 0 ? (
              <p className="wh-preg-detail-empty">No check-ups recorded.</p>
            ) : (
              <ul className="wh-preg-detail-list">
                {historyCheckups.map((c) => (
                  <li key={c.id}>
                    <strong>{c.checkup_type_display || c.checkup_type}</strong> on {formatDate(c.date)}
                    {c.hospital_name && <> &middot; {c.hospital_name}</>}
                    {c.notes && <span className="wh-preg-detail-item-notes"> — {c.notes}</span>}
                  </li>
                ))}
              </ul>
            )}

            {historyDocs.length > 0 && (
              <>
                <h4 className="wh-preg-detail-section">
                  <FontAwesomeIcon icon={faFileLines} /> Documents ({historyDocs.length})
                </h4>
                <ul className="wh-preg-detail-list">
                  {historyDocs.map((d) => (
                    <li key={d.id}>
                      {d.file ? (
                        <a
                          href={d.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wh-preg-detail-link"
                        >
                          <FontAwesomeIcon icon={faFileLines} /> {d.title}
                        </a>
                      ) : (
                        <strong>{d.title}</strong>
                      )}
                      {d.document_type_display && <> — {d.document_type_display}</>}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <h4 className="wh-preg-detail-section">
              <FontAwesomeIcon icon={faChartLine} /> Fetal Movement Sessions ({historyKicks.length})
            </h4>
            {historyKicks.length === 0 ? (
              <p className="wh-preg-detail-empty">No kick sessions recorded.</p>
            ) : (
              <ul className="wh-preg-detail-list">
                {historyKicks.map((k) => (
                  <li key={k.id}>
                    <strong>{k.kick_count} kicks</strong> on {formatDate(k.start_time)}
                    {k.duration_minutes !== null && <> &middot; {k.duration_minutes} min</>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Modal>
    );
  };

  /* ---- Loading / Error ---- */
  if (loading) {
    return (
      <div className="wh-preg-page">
        <section className="wh-preg-banner">
          <Container>
            <BackButton />
            <div className="wh-preg-banner-content">
              <div className="wh-preg-banner-icon">
                <FontAwesomeIcon icon={faBaby} />
              </div>
              <div>
                <h1 className="wh-preg-banner-title">{t('womensHealth:pregnancy.title')}</h1>
                <p className="wh-preg-banner-subtitle">Track your pregnancy journey week by week</p>
              </div>
            </div>
          </Container>
        </section>
        <Container>
          <div className="wh-preg-loading">
            <FontAwesomeIcon icon={faSpinner} spin className="wh-preg-loading-icon" />
            <p>Loading your pregnancy data...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wh-preg-page">
        <section className="wh-preg-banner">
          <Container>
            <BackButton />
            <div className="wh-preg-banner-content">
              <div className="wh-preg-banner-icon">
                <FontAwesomeIcon icon={faBaby} />
              </div>
              <div>
                <h1 className="wh-preg-banner-title">{t('womensHealth:pregnancy.title')}</h1>
                <p className="wh-preg-banner-subtitle">Track your pregnancy journey week by week</p>
              </div>
            </div>
          </Container>
        </section>
        <Container>
          <div className="wh-preg-error">
            <FontAwesomeIcon icon={faTriangleExclamation} className="wh-preg-error-icon" />
            <p>{error}</p>
          </div>
        </Container>
      </div>
    );
  }

  /* ---- No Active Pregnancy: Setup ---- */
  if (!pregnancy) {
    return (
      <div className="wh-preg-page">
        <section className="wh-preg-banner">
          <Container>
            <BackButton />
            <div className="wh-preg-banner-content">
              <div className="wh-preg-banner-icon">
                <FontAwesomeIcon icon={faBaby} />
              </div>
              <div>
                <h1 className="wh-preg-banner-title">{t('womensHealth:pregnancy.title')}</h1>
                <p className="wh-preg-banner-subtitle">Track your pregnancy journey week by week</p>
              </div>
            </div>
          </Container>
        </section>
        <Container>
          {justEnded && (
            <div className="wh-preg-complete-card">
              <div className="wh-preg-complete-icon">
                <FontAwesomeIcon icon={justEnded.status === 'loss' ? faHeartPulse : faFlagCheckered} />
              </div>
              <div className="wh-preg-complete-body">
                <h2>
                  {justEnded.status === 'loss'
                    ? 'We are sorry for your loss'
                    : 'Congratulations, your pregnancy journey is complete'}
                </h2>
                <div className="wh-preg-history-meta wh-preg-complete-meta">
                  {justEnded.weeks_carried !== null && (
                    <span><strong>{justEnded.weeks_carried}</strong> weeks carried</span>
                  )}
                  {justEnded.delivery_date && <span>Ended {formatDate(justEnded.delivery_date)}</span>}
                  {justEnded.delivery_type && justEnded.delivery_type !== 'undecided' && (
                    <span>{justEnded.delivery_type_display || justEnded.delivery_type}</span>
                  )}
                </div>
                {justEnded.outcome_notes && (
                  <p className="wh-preg-detail-notes">
                    <FontAwesomeIcon icon={faNotesMedical} /> {justEnded.outcome_notes}
                  </p>
                )}
                <div className="wh-preg-complete-actions">
                  {justEnded.status === 'delivered' && (
                    <Link
                      className="wh-preg-submit-btn"
                      to={`/womens-health/postpartum-recovery?pregnancy=${justEnded.id}`}
                    >
                      <FontAwesomeIcon icon={faHeartPulse} /> Start Postpartum Recovery
                    </Link>
                  )}
                  <button
                    type="button"
                    className="wh-preg-ghost-btn"
                    onClick={() => openHistoryDetail(justEnded)}
                  >
                    <FontAwesomeIcon icon={faClipboardList} /> View records
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="wh-preg-setup-card">
            <div className="wh-preg-setup-header">
              <div className="wh-preg-setup-icon">
                <FontAwesomeIcon icon={faSeedling} />
              </div>
              <h2>{history.length > 0 ? 'Start a New Pregnancy' : 'Start Your Pregnancy Journey'}</h2>
              <p>Enter your details to begin tracking your pregnancy week by week.</p>
            </div>
            <form className="wh-preg-setup-form" onSubmit={handleSetupSubmit}>
              <div className="wh-preg-form-group">
                <label>Last Menstrual Period (LMP) Date</label>
                <input
                  type="date"
                  value={setupForm.lmp_date}
                  onChange={(e) => setSetupForm({ ...setupForm, lmp_date: e.target.value })}
                  required
                  className="wh-preg-input"
                />
              </div>
              <div className="wh-preg-form-group">
                <label>Delivery Type (planned)</label>
                <select
                  value={setupForm.delivery_type}
                  onChange={(e) => setSetupForm({ ...setupForm, delivery_type: e.target.value })}
                  className="wh-preg-select"
                >
                  {choices?.delivery_type ? (
                    choices.delivery_type.map((c: any) => (
                      <option key={c.value} value={c.value}>
                        {c.display_name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="undecided">Undecided</option>
                      <option value="natural">Natural/Vaginal</option>
                      <option value="c_section">C-Section</option>
                      <option value="vbac">VBAC</option>
                    </>
                  )}
                </select>
              </div>
              <button type="submit" className="wh-preg-submit-btn" disabled={submittingSetup}>
                {submittingSetup ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Creating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faArrowRight} /> Start Tracking
                  </>
                )}
              </button>
            </form>
          </div>
          {renderHistory()}
          {renderHistoryDetail()}
          {toast && (
            <div className={`wh-preg-toast wh-preg-toast-${toast.type}`}>
              <FontAwesomeIcon icon={toast.type === 'success' ? faCheck : faXmark} />
              {toast.message}
            </div>
          )}
        </Container>
      </div>
    );
  }

  /* ---- Active Pregnancy Dashboard ---- */
  const currentWeek = effectiveWeek;
  const babyDev = getBabyDevelopment(currentWeek);

  const renderThisWeek = () => {
    if (!latestLog) {
      return (
        <div className="wh-preg-empty-state">
          <div className="wh-preg-empty-icon">
            <FontAwesomeIcon icon={faNotesMedical} />
          </div>
          <h3>No Logs Yet</h3>
          <p>Start logging your weekly updates to track your pregnancy journey.</p>
          <button className="wh-preg-link-btn" onClick={() => setActiveTab('log-week')}>
            <FontAwesomeIcon icon={faPlus} /> Log This Week
          </button>
        </div>
      );
    }

    return (
      <div className="wh-preg-this-week">
        <div className="wh-preg-week-cards">
          <div className="wh-preg-week-card">
            <span className="wh-preg-week-card-label">Mood</span>
            <span className="wh-preg-week-card-value">
              <FontAwesomeIcon icon={faFaceSmile} style={{ color: '#ec4899' }} />
              {latestLog.mood_display || latestLog.mood || 'Not logged'}
            </span>
          </div>
          <div className="wh-preg-week-card">
            <span className="wh-preg-week-card-label">Weight</span>
            <span className="wh-preg-week-card-value">
              <FontAwesomeIcon icon={faWeightScale} style={{ color: '#8b5cf6' }} />
              {latestLog.weight ? `${latestLog.weight} kg` : 'Not logged'}
            </span>
          </div>
          <div className="wh-preg-week-card">
            <span className="wh-preg-week-card-label">Fetal Movements</span>
            <span className="wh-preg-week-card-value">
              <FontAwesomeIcon icon={faBaby} style={{ color: '#06b6d4' }} />
              {latestLog.fetal_movement_count !== null ? latestLog.fetal_movement_count : 'Not logged'}
            </span>
          </div>
          <div className="wh-preg-week-card">
            <span className="wh-preg-week-card-label">Week Logged</span>
            <span className="wh-preg-week-card-value">
              <FontAwesomeIcon icon={faCalendarDays} style={{ color: '#f59e0b' }} />
              Week {latestLog.week_number}
            </span>
          </div>
        </div>

        {latestLog.symptom_details && latestLog.symptom_details.length > 0 && (
          <div className="wh-preg-symptoms-section">
            <h4>Symptoms This Week</h4>
            <div className="wh-preg-symptom-chips">
              {latestLog.symptom_details.map((s) => (
                <span className="wh-preg-symptom-chip" key={s.id}>{s.name}</span>
              ))}
            </div>
          </div>
        )}

        {latestLog.notes && (
          <div className="wh-preg-notes-section">
            <h4>Notes</h4>
            <p>{latestLog.notes}</p>
          </div>
        )}
      </div>
    );
  };

  const renderLogWeek = () => (
    <div className="wh-preg-log-week">
      <form className="wh-preg-log-form" onSubmit={handleLogSubmit}>
        <div className="wh-preg-form-row">
          <div className="wh-preg-form-group">
            <label>Week Number</label>
            <input
              type="number"
              min={1}
              max={42}
              value={logForm.week_number}
              onChange={(e) => setLogForm({ ...logForm, week_number: parseInt(e.target.value, 10) || 1 })}
              className="wh-preg-input"
            />
          </div>
          <div className="wh-preg-form-group">
            <label>Weight (kg)</label>
            <input
              type="number"
              step="0.1"
              placeholder="e.g. 65.5"
              value={logForm.weight}
              onChange={(e) => setLogForm({ ...logForm, weight: e.target.value })}
              className="wh-preg-input"
            />
          </div>
        </div>

        <div className="wh-preg-form-row">
          <div className="wh-preg-form-group">
            <label>Mood</label>
            <select
              value={logForm.mood}
              onChange={(e) => setLogForm({ ...logForm, mood: e.target.value })}
              className="wh-preg-select"
            >
              <option value="">Select mood</option>
              {weekLogChoices?.mood ? (
                weekLogChoices.mood.map((c: any) => (
                  <option key={c.value} value={c.value}>{c.display_name}</option>
                ))
              ) : (
                <>
                  <option value="happy">Happy</option>
                  <option value="calm">Calm</option>
                  <option value="anxious">Anxious</option>
                  <option value="tired">Tired</option>
                  <option value="sad">Sad</option>
                  <option value="irritable">Irritable</option>
                </>
              )}
            </select>
          </div>
          <div className="wh-preg-form-group">
            <label>Fetal Movement Count</label>
            <input
              type="number"
              min={0}
              placeholder="e.g. 10"
              value={logForm.fetal_movement_count}
              onChange={(e) => setLogForm({ ...logForm, fetal_movement_count: e.target.value })}
              className="wh-preg-input"
            />
          </div>
        </div>

        {symptoms.length > 0 && (
          <div className="wh-preg-form-group">
            <label>Symptoms</label>

            <div
              className="wh-preg-symptoms-grid"
              style={{ display: "grid", gap: "12px" }}
            >
              {symptoms.map((s) => (
                <label
                  className="wh-preg-symptom-checkbox"
                  key={s.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={logForm.symptoms.includes(s.id)}
                    onChange={() => toggleSymptom(s.id)}
                  />

                  <span className="wh-preg-checkbox-label">{s.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="wh-preg-form-group">
          <label>Notes</label>
          <textarea
            rows={3}
            placeholder="How are you feeling this week?"
            value={logForm.notes}
            onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
            className="wh-preg-textarea"
          />
        </div>

        <button type="submit" className="wh-preg-submit-btn" disabled={submittingLog}>
          {submittingLog ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Saving...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} /> Save Week Log
            </>
          )}
        </button>
      </form>
    </div>
  );

  const renderWeightHistory = () => {
    const sortedLogs = [...weekLogs].sort((a, b) => a.week_number - b.week_number);

    if (sortedLogs.length === 0) {
      return (
        <div className="wh-preg-empty-state">
          <div className="wh-preg-empty-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <h3>No Weight Data</h3>
          <p>Start logging your weekly updates to see your weight history.</p>
        </div>
      );
    }

    return (
      <div className="wh-preg-weight-history">
        <div className="wh-preg-table-wrapper">
          <table className="wh-preg-table">
            <thead>
              <tr>
                <th>Week</th>
                <th>Weight (kg)</th>
                <th>Mood</th>
                <th>Fetal Movements</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedLogs.map((log) => (
                <tr key={log.id}>
                  {editingLogId === log.id ? (
                    <>
                      <td>Week {log.week_number}</td>
                      <td>
                        <input
                          type="number"
                          step="0.1"
                          value={editForm.weight}
                          onChange={(e) => setEditForm({ ...editForm, weight: e.target.value })}
                          className="wh-preg-input-sm"
                        />
                      </td>
                      <td>
                        <select
                          value={editForm.mood}
                          onChange={(e) => setEditForm({ ...editForm, mood: e.target.value })}
                          className="wh-preg-select-sm"
                        >
                          <option value="">--</option>
                          {weekLogChoices?.mood?.map((c: any) => (
                            <option key={c.value} value={c.value}>{c.display_name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          value={editForm.fetal_movement_count}
                          onChange={(e) =>
                            setEditForm({ ...editForm, fetal_movement_count: e.target.value })
                          }
                          className="wh-preg-input-sm"
                        />
                      </td>
                      <td>{formatDate(log.created_at)}</td>
                      <td>
                        <div className="wh-preg-action-btns">
                          <button className="wh-preg-action-btn wh-preg-save-btn" onClick={() => handleEditSave(log.id)}>
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                          <button className="wh-preg-action-btn wh-preg-cancel-btn" onClick={() => setEditingLogId(null)}>
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td><span className="wh-preg-week-badge">Week {log.week_number}</span></td>
                      <td>{log.weight ? `${log.weight} kg` : '-'}</td>
                      <td>{log.mood_display || log.mood || '-'}</td>
                      <td>{log.fetal_movement_count ?? '-'}</td>
                      <td>{formatDate(log.created_at)}</td>
                      <td>
                        <div className="wh-preg-action-btns">
                          <button className="wh-preg-action-btn wh-preg-edit-btn" onClick={() => handleEditStart(log)}>
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                          <button className="wh-preg-action-btn wh-preg-delete-btn" onClick={() => handleDeleteLog(log.id)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderFetalMovement = () => {
    const sortedLogs = [...weekLogs].sort((a, b) => a.week_number - b.week_number);
    const withMovement = sortedLogs.filter((l) => l.fetal_movement_count !== null && l.fetal_movement_count !== undefined);

    if (withMovement.length === 0) {
      return (
        <div className="wh-preg-empty-state">
          <div className="wh-preg-empty-icon">
            <FontAwesomeIcon icon={faBaby} />
          </div>
          <h3>No Fetal Movement Data</h3>
          <p>Log your weekly fetal movement (kick) counts to track your baby's activity over time.</p>
          <button className="wh-preg-link-btn" onClick={() => setActiveTab('log-week')}>
            <FontAwesomeIcon icon={faPlus} /> Log This Week
          </button>
        </div>
      );
    }

    return (
      <div className="wh-preg-weight-history">
        <div className="wh-preg-table-wrapper">
          <table className="wh-preg-table">
            <thead>
              <tr>
                <th>Week</th>
                <th>Fetal Movements</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {withMovement.map((log) => (
                <tr key={log.id}>
                  <td><span className="wh-preg-week-badge">Week {log.week_number}</span></td>
                  <td>
                    <FontAwesomeIcon icon={faBaby} style={{ color: '#06b6d4', marginRight: 6 }} />
                    {log.fetal_movement_count}
                  </td>
                  <td>{formatDate(log.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'this-week':
        return renderThisWeek();
      case 'log-week':
        return renderLogWeek();
      case 'weight-history':
        return renderWeightHistory();
      case 'fetal-movement':
        return renderFetalMovement();
      default:
        return null;
    }
  };

  return (
    <div className="wh-preg-page">
      {/* Banner */}
      <section className="wh-preg-banner">
        <Container>
          <BackButton />
          <div className="wh-preg-banner-content">
            <div className="wh-preg-banner-icon">
              <FontAwesomeIcon icon={faBaby} />
            </div>
            <div>
              <h1 className="wh-preg-banner-title">{t('womensHealth:pregnancy.title')}</h1>
              <p className="wh-preg-banner-subtitle">Track your pregnancy journey week by week</p>
            </div>
          </div>
        </Container>
      </section>

      <Container>
        {/* Hero: Pregnancy Profile + Week Tracker */}
        <div className="wh-preg-hero">
          <div className="wh-preg-hero-main">
            <div className="wh-preg-hero-head">
              <h2 className="wh-preg-hero-head-title">
                <FontAwesomeIcon icon={faVenus} />
                Pregnancy Profile
              </h2>
              <div className="wh-preg-hero-actions">
                <button
                  className="wh-preg-ghost-btn"
                  onClick={handleProfileEditStart}
                >
                  <FontAwesomeIcon icon={faPen} /> Edit Start Date
                </button>
                <button
                  className="wh-preg-ghost-btn"
                  onClick={handleEndStart}
                >
                  <FontAwesomeIcon icon={faFlagCheckered} /> End Pregnancy
                </button>
                <button
                  className="wh-preg-ghost-btn wh-preg-ghost-btn-danger"
                  onClick={handleProfileDelete}
                >
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              </div>
            </div>
            <div className="wh-preg-hero-body">
              <div className="wh-preg-hero-week">
                <span className="wh-preg-hero-week-num">{currentWeek}</span>
                <span className="wh-preg-hero-week-label">weeks</span>
              </div>
              <div className="wh-preg-hero-info">
                <p className="wh-preg-hero-trimester">{getTrimester(currentWeek)}</p>
                {pregnancy.lmp_date && (
                  <p className="wh-preg-hero-edd">
                    <FontAwesomeIcon icon={faCalendarDays} /> LMP: {formatDate(pregnancy.lmp_date)}
                  </p>
                )}
                {pregnancy.edd && (
                  <p className="wh-preg-hero-edd">
                    <FontAwesomeIcon icon={faCalendarDays} /> Due Date: {formatDate(pregnancy.edd)}
                  </p>
                )}
                <div className="wh-preg-progress-bar">
                  <div className="wh-preg-progress-fill" style={{ width: `${progressPercent}%` }}></div>
                </div>
                <p className="wh-preg-progress-text">
                  {progressPercent.toFixed(0)}% complete &middot; {Math.max(40 - currentWeek, 0)} weeks to go
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Baby Development */}
        <div className="wh-preg-baby-dev">
          <div className="wh-preg-baby-dev-icon">
            <span className="wh-preg-fruit-emoji">{babyDev.fruit}</span>
          </div>
          <div className="wh-preg-baby-dev-info">
            <h3 className="wh-preg-baby-dev-title">
              Baby is the size of a {babyDev.fruit}
            </h3>
            <p className="wh-preg-baby-dev-size">Approximately {babyDev.size}</p>
            <p className="wh-preg-baby-dev-milestone">{babyDev.milestone}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="wh-preg-tabs-wrapper">
          <div className="wh-preg-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`wh-preg-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <FontAwesomeIcon icon={tab.icon} className="wh-preg-tab-icon" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="wh-preg-content" key={activeTab}>
          {renderTabContent()}
        </div>

        {renderHistory()}

        {/* Toast */}
        {toast && (
          <div className={`wh-preg-toast wh-preg-toast-${toast.type}`}>
            <FontAwesomeIcon icon={toast.type === 'success' ? faCheck : faXmark} />
            {toast.message}
          </div>
        )}

        {/* Edit-profile (LMP / delivery type) modal */}
        <Modal
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
          title="Edit Pregnancy Details"
          icon={faVenus}
          size="md"
          footer={
            <>
              <button
                type="button"
                className="wh-preg-ghost-btn"
                onClick={() => setEditProfileOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="preg-edit-form"
                disabled={savingProfile}
                className="wh-preg-ghost-btn"
                style={{ background: '#ec4899', color: '#fff', borderColor: '#ec4899' }}
              >
                {savingProfile ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : 'Save'}
              </button>
            </>
          }
        >
          <form id="preg-edit-form" onSubmit={handleProfileSave}>
            <div className="wh-preg-form-group">
              <label>Last Menstrual Period (LMP) Date</label>
              <input
                type="date"
                value={profileForm.lmp_date}
                onChange={(e) => setProfileForm({ ...profileForm, lmp_date: e.target.value })}
                required
                className="wh-preg-input"
              />
            </div>
            <div className="wh-preg-form-group">
              <label>Delivery Type (planned)</label>
              <select
                value={profileForm.delivery_type}
                onChange={(e) => setProfileForm({ ...profileForm, delivery_type: e.target.value })}
                className="wh-preg-select"
              >
                {choices?.delivery_type ? (
                  choices.delivery_type.map((c: any) => (
                    <option key={c.value} value={c.value}>{c.display_name}</option>
                  ))
                ) : (
                  <>
                    <option value="undecided">Undecided</option>
                    <option value="natural">Natural/Vaginal</option>
                    <option value="c_section">C-Section</option>
                    <option value="vbac">VBAC</option>
                  </>
                )}
              </select>
            </div>
          </form>
        </Modal>

        {/* End-pregnancy (delivered / loss) modal */}
        <Modal
          open={endOpen}
          onClose={() => setEndOpen(false)}
          title="End Pregnancy"
          icon={faFlagCheckered}
          size="md"
          footer={
            <>
              <button
                type="button"
                className="wh-preg-ghost-btn"
                onClick={() => setEndOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="preg-end-form"
                disabled={endingPregnancy}
                className="wh-preg-ghost-btn"
                style={{ background: '#ec4899', color: '#fff', borderColor: '#ec4899' }}
              >
                {endingPregnancy ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFlagCheckered} />
                    {endForm.status === 'delivered' ? 'Mark Delivered' : 'Record Loss'}
                  </>
                )}
              </button>
            </>
          }
        >
          <form id="preg-end-form" onSubmit={handleEndSubmit}>
            {endForm.status === 'delivered' ? (
              <div className="wh-preg-form-group">
                <div className="wh-preg-end-positive">
                  <FontAwesomeIcon icon={faFlagCheckered} />
                  <div>
                    <strong>My baby has arrived</strong>
                    <span>Record the delivery and move this pregnancy into your history.</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="wh-preg-end-loss-link"
                  onClick={() => setEndForm({ ...endForm, status: 'loss' })}
                >
                  The pregnancy ended in loss
                </button>
              </div>
            ) : (
              <div className="wh-preg-form-group">
                <div className="wh-preg-end-loss">
                  <FontAwesomeIcon icon={faHeartPulse} />
                  <div>
                    <strong>We are so sorry</strong>
                    <span>
                      There is no right way to feel, and no rush. This is saved privately to
                      your history.
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="wh-preg-end-loss-link"
                  onClick={() => setEndForm({ ...endForm, status: 'delivered' })}
                >
                  Go back — my baby has arrived
                </button>
              </div>
            )}

            <div className="wh-preg-form-row">
              <div className="wh-preg-form-group">
                <label>{endForm.status === 'delivered' ? 'Delivery Date' : 'Date of Loss'}</label>
                <input
                  type="date"
                  value={endForm.delivery_date}
                  onChange={(e) => setEndForm({ ...endForm, delivery_date: e.target.value })}
                  required
                  className="wh-preg-input"
                />
              </div>
              {endForm.status === 'delivered' && (
                <div className="wh-preg-form-group">
                  <label>Delivery Type</label>
                  <select
                    value={endForm.delivery_type}
                    onChange={(e) => setEndForm({ ...endForm, delivery_type: e.target.value })}
                    className="wh-preg-select"
                  >
                    {choices?.delivery_type ? (
                      choices.delivery_type.map((c: any) => (
                        <option key={c.value} value={c.value}>{c.display_name}</option>
                      ))
                    ) : (
                      <>
                        <option value="undecided">Undecided</option>
                        <option value="natural">Natural/Vaginal</option>
                        <option value="c_section">C-Section</option>
                        <option value="vbac">VBAC</option>
                      </>
                    )}
                  </select>
                </div>
              )}
            </div>

            <div className="wh-preg-form-group">
              <label>Notes (optional)</label>
              <textarea
                rows={3}
                placeholder={endForm.status === 'delivered' ? 'How did the birth go?' : 'Anything you would like to remember'}
                value={endForm.outcome_notes}
                onChange={(e) => setEndForm({ ...endForm, outcome_notes: e.target.value })}
                className="wh-preg-textarea"
              />
            </div>

            {endForm.status === 'delivered' && (
              <p className="wh-preg-end-hint">
                This closes the pregnancy and moves it to your history. You can then start
                postpartum recovery tracking.
              </p>
            )}
            {endForm.status === 'loss' && (
              <p className="wh-preg-end-hint">
                This records the pregnancy as a loss and moves it to your history. Take care of
                yourself — support resources are available under Postpartum.
              </p>
            )}
          </form>
        </Modal>

        {/* Read-only view of a past pregnancy */}
        {renderHistoryDetail()}

        {/* Confirm dialog */}
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel || 'Delete'}
          cancelLabel="Cancel"
          variant={confirmDialog.variant || 'danger'}
          onConfirm={confirmDialog.onConfirm}
          onClose={() => setConfirmDialog((d) => ({ ...d, open: false }))}
        />
      </Container>
    </div>
  );
};

export default PregnancyDashboard;
